import React from 'react';
import { Document, Page, Text, View, StyleSheet, Circle, Svg, Path, Image } from '@react-pdf/renderer';

// Define styles for PDF rendering
const styles = StyleSheet.create({
  page: {
    flexDirection: 'column',
    backgroundColor: '#F9FAFB',
    padding: 30,
  },
  container: {
    maxWidth: '100%',
    margin: 'auto',
    backgroundColor: '#F9FAFB',
    borderRadius: 8,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  badge: {
    borderWidth: 1,
    borderColor: '#4B5563',
    borderRadius: 999,
    paddingVertical: 4,
    paddingHorizontal: 16,
  },
  badgeText: {
    fontSize: 10,
    color: '#4B5563',
  },
  dateText: {
    fontSize: 10,
    color: '#4B5563',
  },
  title: {
    fontSize: 24,
    fontWeight: 'bold',
    marginBottom: 8,
    color: '#1F2937',
  },
  subtitle: {
    fontSize: 12,
    marginBottom: 24,
    color: '#6B7280',
  },
  section: {
    marginBottom: 24,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: '300',
    marginBottom: 8,
    color: '#1F2937',
  },
  progressBar: {
    height: 16,
    flexDirection: 'row',
    borderRadius: 8,
    overflow: 'hidden',
    marginTop: 8,
  },
  progressBarLabel: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 4,
  },
  labelText: {
    fontSize: 10,
  },
  card: {
    backgroundColor: '#FFFFFF',
    borderRadius: 8,
    padding: 24,
    marginBottom: 24,
    borderWidth: 1,
    borderColor: '#F3F4F6',
  },
  row: {
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  divider: {
    borderBottomWidth: 1,
    borderBottomColor: '#E5E7EB',
    marginVertical: 24,
  },
  emotionsContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 16,
    marginBottom: 16,
  },
  emotionItem: {
    alignItems: 'center',
    width: '20%',
  },
  emotionCircle: {
    width: 90,
    height: 90,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 4,
    position: 'relative',
  },
  emotionLabel: {
    marginTop: 4,
    paddingVertical: 4,
    paddingHorizontal: 8,
    fontSize: 8,
    color: '#FFFFFF',
    borderRadius: 4,
    textAlign: 'center',
  },
  emotionCount: {
    fontSize: 10,
    textAlign: 'center',
    marginTop: 4,
    fontWeight: 'bold',
  },
  circleText: {
    position: 'absolute',
    fontSize: 12,
    fontWeight: 'bold',
    textAlign: 'center',
  },
  pieContainer: {
    width: '67%',
    alignItems: 'center',
    justifyContent: 'center',
  },
  legendContainer: {
    width: '33%',
    justifyContent: 'center',
  },
  legendItem: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
  },
  legendIcon: {
    width: 15,
    height: 15,
    marginRight: 8,
    objectFit: "contain",
  },
  legendText: {
    fontSize: 8,
    textTransform: 'uppercase',
  },
  emptyStatePie: {
    width: 200,
    height: 200,
    justifyContent: 'center',
    alignItems: 'center',
  },
  percentageText: {
    fontSize: 12,
    fontWeight: 'bold',
  },
  highlightText: {
    fontWeight: 'normal',
  },
  descriptionText: {
    fontSize: 12,
    marginBottom: 24,
    color: '#4B5563',
  },
  boldText: {
    fontWeight: '600',
  }
});

// Progress Bar component for PDF
const ProgressBar = ({ settledPercentage, unsettledPercentage }) => (
  <View>
    <View style={styles.progressBarLabel}>
      <Text style={styles.labelText}>{settledPercentage}% Settled</Text>
      <Text style={styles.labelText}>{unsettledPercentage}% Unsettled</Text>
    </View>
    <View style={styles.progressBar}>
      <View style={{
        backgroundColor: '#A7F3D0', // teal-200 equivalent
        width: `${settledPercentage}%`,
        height: '100%'
      }} />
      <View style={{
        backgroundColor: '#FECACA', // red-200 equivalent
        width: `${unsettledPercentage}%`,
        height: '100%'
      }} />
    </View>
  </View>
);

// Update the EmotionCircle component with a different approach for react-pdf
const EmotionCircle = ({ percentage, color, label, count }) => {
  const circleSize = 90;
  const strokeWidth = 8;
  const radius = (circleSize - strokeWidth) / 2;

  // Convert percentage to angle (0-360)
  const angle = (percentage / 100) * 360;
  
  // Create an SVG path for an arc without filling
  const describeArc = (x, y, radius, startAngleDegrees, endAngleDegrees) => {
    // Convert to radians
    const startAngleRad = (startAngleDegrees - 90) * Math.PI / 180;
    const endAngleRad = (endAngleDegrees - 90) * Math.PI / 180;
    
    const x1 = x + radius * Math.cos(startAngleRad);
    const y1 = y + radius * Math.sin(startAngleRad);
    const x2 = x + radius * Math.cos(endAngleRad);
    const y2 = y + radius * Math.sin(endAngleRad);
    
    const largeArcFlag = (endAngleDegrees - startAngleDegrees <= 180) ? "0" : "1";
    
    // For 100%, use a complete circle path
    if (endAngleDegrees - startAngleDegrees >= 359.99) {
      // Use two semicircles for a complete circle
      return `M ${x1} ${y1} A ${radius} ${radius} 0 1 1 ${x1-0.1} ${y1-0.1}`;
    }
    
    // Just draw the arc, don't include center point
    return `M ${x1} ${y1} A ${radius} ${radius} 0 ${largeArcFlag} 1 ${x2} ${y2}`;
  };
  
  return (
    <View style={styles.emotionItem}>
      <View style={{width: circleSize, height: circleSize, position: 'relative'}}>
        <Svg width={circleSize} height={circleSize}>
          {/* Background circle */}
          <Circle cx={circleSize/2} cy={circleSize/2} r={radius} fill="#E6EDF7" />
          
          {/* Inner white circle */}
          <Circle cx={circleSize/2} cy={circleSize/2} r={radius-5} fill="white" />
          
          {/* Draw the arc using Path instead of relying on Circle with fill="transparent" */}
          {percentage > 0 && (
            <Path 
              d={describeArc(circleSize/2, circleSize/2, radius, 0, angle)} 
              stroke={color}
              strokeWidth={strokeWidth}
              // Using "none" instead of "transparent" for more consistent PDF rendering
              fill="none"
              strokeLinecap="round"
            />
          )}
        </Svg>
        
        {/* Centered percentage text */}
        <View style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          justifyContent: 'center',
          alignItems: 'center',
        }}>
          <Text style={{
            fontSize: 12,
            fontWeight: 'bold',
            color: '#000000',
          }}>{percentage}%</Text>
        </View>
      </View>
      
      <View style={{ 
        backgroundColor: color, 
        marginTop: 4,
        paddingVertical: 4,
        paddingHorizontal: 16,
        borderRadius: 8,
      }}>
        <Text style={{ 
          fontSize: 8, 
          color: '#FFFFFF', 
          textAlign: 'center'
        }}>
          {label}
        </Text>
      </View>
      
      <Text style={styles.emotionCount}>{count}</Text>
    </View>
  );
};

// Pie Chart component for PDF
const PieChart = ({ segments }) => {
  // Filter out segments with 0 percentage before calculating
  const filteredSegments = segments.filter(segment => segment.percentage > 0);

  // Calculate total percentage of all segments
  const totalPercentage = filteredSegments.reduce((sum, segment) => sum + segment.percentage, 0);

  const pieSize = 220;
  const centerX = pieSize / 2;
  const centerY = pieSize / 2;
  const radius = pieSize * 0.45;

  // If there are no valid segments, show an empty circle
  if (!filteredSegments || filteredSegments.length === 0) {
    return (
      <View style={styles.emptyStatePie}>
        <Svg width={pieSize} height={pieSize} viewBox={`0 0 ${pieSize} ${pieSize}`}>
          <Circle cx={centerX} cy={centerY} r={radius} fill="#E6EDF7" />
          <Text
            x={centerX}
            y={centerY}
            style={{ textAnchor: 'middle', fontSize: 12 }}
          >
            No data
          </Text>
        </Svg>
      </View>
    );
  }

  // If there's only one segment with 100%, draw a full circle
  if (filteredSegments.length === 1 && Math.round(filteredSegments[0].percentage) === 100) {
    return (
      <View style={styles.emptyStatePie}>
        <Svg width={pieSize} height={pieSize} viewBox={`0 0 ${pieSize} ${pieSize}`}>
          <Circle cx={centerX} cy={centerY} r={radius} fill={filteredSegments[0].color} />
          <Text
            x={centerX}
            y={centerY}
            style={{
              textAnchor: 'middle',
              fontSize: 16,
              fontWeight: 'bold',
              color: 'white'
            }}
          >
            100%
          </Text>
        </Svg>
      </View>
    );
  }

  // For multiple segments, create pie slices
  const paths = [];

  // Add gray background circle if total percentage is less than 100%
  if (totalPercentage < 100) {
    paths.push(
      <Circle
        key="background"
        cx={centerX}
        cy={centerY}
        r={radius}
        fill="#E6EDF7"
      />
    );
  }

  // For multiple segments, create pie slices
  let cumulativeAngle = 0;

  filteredSegments.forEach((segment, index) => {
    const startAngle = cumulativeAngle;
    const angleRadians = (segment.percentage / 100) * 2 * Math.PI;
    cumulativeAngle += angleRadians;

    const x1 = centerX + radius * Math.cos(startAngle);
    const y1 = centerY + radius * Math.sin(startAngle);
    const x2 = centerX + radius * Math.cos(startAngle + angleRadians);
    const y2 = centerY + radius * Math.sin(startAngle + angleRadians);

    const largeArcFlag = segment.percentage > 50 ? 1 : 0;

    const pathData = `M ${centerX} ${centerY} L ${x1} ${y1} A ${radius} ${radius} 0 ${largeArcFlag} 1 ${x2} ${y2} Z`;

    paths.push(
      <Path
        key={`path-${index}`}
        d={pathData}
        fill={segment.color}
      />
    );
  });

  // Update the pie chart text rendering to avoid overlap
  filteredSegments.forEach((segment, index) => {
    const startAngle = (index === 0) ? 0 :
      filteredSegments.slice(0, index).reduce((sum, s) => sum + (s.percentage / 100) * 2 * Math.PI, 0);
    const angleRadians = (segment.percentage / 100) * 2 * Math.PI;

    // Add percentage labels for segments with >= 5%
    if (segment.percentage >= 5) {
      const labelAngle = startAngle + (angleRadians / 2);

      // Adjust distance based on segment size to avoid overlap
      let distanceMultiplier = 0.6; // Default distance

      // For very small segments, move text slightly outward
      if (segment.percentage < 10) {
        distanceMultiplier = 0.7;
      }
      // For larger segments, keep text more centered
      else if (segment.percentage > 25) {
        distanceMultiplier = 0.5;
      }

      const distanceFromCenter = radius * distanceMultiplier;

      const labelX = centerX + distanceFromCenter * Math.cos(labelAngle);
      const labelY = centerY + distanceFromCenter * Math.sin(labelAngle);

      // Add the percentage text with proper spacing
      // Use a monospace-style approach where the font width is predictable
      paths.push(
        <Text
          key={`text-${index}`}
          x={labelX - 6}
          y={labelY}
          style={{
            textAnchor: 'middle',
            fontSize: 10,
            fontWeight: 'bold',
            fill: 'white',
          }}
        >
          {Math.round(segment.percentage)}
        </Text>
      );

      // Add the % symbol as a separate text element
      paths.push(
        <Text
          key={`symbol-${index}`}
          x={labelX + 6} // Offset to the right
          y={labelY}
          style={{
            textAnchor: 'middle',
            fontSize: 9, // Slightly smaller font
            fontWeight: 'bold',
            fill: 'white'
          }}
        >
          %
        </Text>
      );
    }
  });

  return (
    <View style={{ width: pieSize, height: pieSize }}>
      <Svg width={pieSize} height={pieSize} viewBox={`0 0 ${pieSize} ${pieSize}`}>
        {paths}
      </Svg>
    </View>
  );
};

// Create the PDF document component
const InfoGraphicPdfGenerator = ({ data }) => {
  // Ensure data has proper fallback values
  if (!data) {
    return (
      <Document>
        <Page size="A4" style={styles.page}>
          <View style={styles.container}>
            <Text style={styles.title}>Error: No data available for report generation</Text>
          </View>
        </Page>
      </Document>
    );
  }

  // Process the data like InfoGraphicReportPreview does
  let processedData = data;

  try {
    // Only process if we're dealing with the raw API data format (not already transformed)
    if (!data.emotionsList && (data.dynamic || data.ResponsePercentage)) {
      const jsonData = data;
      const FALLBACK_COLOR = "#c9d6e3";

      // Prefer the dynamic block (id/code-based, includes the new feelings and
      // positive reasons). Fall back to legacy keys if it isn't present.
      const summary = jsonData?.dynamic?.summary;

      let emotionsList = [];
      let highestEmotionName = "N/A";
      let highestEmotionPercentage = 0;
      let settled = 0;
      let unsettled = 0;
      let overall = 0;
      let mainFactor = "None";
      let mainFactorPercentage = 0;
      let availableReasons = [];

      if (summary) {
        // ---- Dynamic path ----
        const feelings = summary.feelings || [];

        emotionsList = feelings.map((f) => ({
          name: f.name,
          color: f.color || FALLBACK_COLOR,
          percentage: Math.round(Number(f.percentage) || 0),
          count: f.count || 0,
        }));

        const topFeeling = feelings.reduce((top, f) => (f.count > (top?.count || 0) ? f : top), null);
        if (topFeeling) {
          highestEmotionName = topFeeling.name;
          highestEmotionPercentage = Math.round(Number(topFeeling.percentage) || 0);
        }

        const polaritySplit = summary.polarity_split || {};
        settled = Math.round(Number(polaritySplit.healthy_percentage) || 0);
        unsettled = Math.round(Number(polaritySplit.unhealthy_percentage) || 0);
        overall = settled;

        availableReasons = (summary.negative_reasons || [])
          .filter((r) => (Number(r.percentage) || 0) > 0)
          .map((r) => ({ name: r.name, percentage: Number(r.percentage) || 0, color: r.color || FALLBACK_COLOR, icon_data: r.icon_data }));

        const topReason = availableReasons.reduce((top, r) => (r.percentage > (top?.percentage || 0) ? r : top), null);
        if (topReason) {
          mainFactor = topReason.name;
          mainFactorPercentage = topReason.percentage;
        }
      } else {
        // ---- Legacy fallback path ----
        const rp = jsonData.ResponsePercentage || {};
        const totalResponses =
          (rp.greatTotal || 0) + (rp.okTotal || 0) + (rp.mehTotal || 0) + (rp.lowTotal || 0) + (rp.strugglingTotal || 0);

        const legacyFeelings = [
          { name: "Great", color: "#FF9E9E", count: rp.greatTotal || 0 },
          { name: "Okay", color: "#FFBE9D", count: rp.okTotal || 0 },
          { name: "Meh", color: "#FFE6BC", count: rp.mehTotal || 0 },
          { name: "Low", color: "#80DBC8", count: rp.lowTotal || 0 },
          { name: "Struggling", color: "#1D3557", count: rp.strugglingTotal || 0 },
        ];

        emotionsList = legacyFeelings.map((f) => ({
          ...f,
          percentage: totalResponses > 0 ? Math.round((f.count / totalResponses) * 100) : 0,
        }));

        const topFeeling = legacyFeelings.reduce((top, f) => (f.count > (top?.count || 0) ? f : top), null);
        if (topFeeling) {
          highestEmotionName = topFeeling.name;
          highestEmotionPercentage = totalResponses > 0 ? Math.round((topFeeling.count / totalResponses) * 100) : 0;
        }

        settled = rp.greate_ok_percentage || 0;
        unsettled = rp.meh_low_struggling_percentage || 0;
        overall = settled;

        const reasonTypes = [
          { key: "poorSleep_percentage", name: "Poor Sleep", color: "#264254" },
          { key: "unwell_percentage", name: "Unwell", color: "#FEDEBC" },
          { key: "relationship_problems_percentage", name: "Relationship Problems", color: "#FCEBB0" },
          { key: "workStress_percentage", name: "Work Related Stress", color: "#F0D2C9" },
          { key: "homeLifeDifficulty_percentage", name: "Home Life Difficulties", color: "#FFBF9D" },
          { key: "bereavement_percentage", name: "Bereavement", color: "#B6E7CF" },
          { key: "anxiety_percentage", name: "Anxiety", color: "#B2C7D9" },
          { key: "cost_of_living_percentage", name: "Cost Of Living", color: "#F7E6AD" },
          { key: "depression_percentage", name: "Depression", color: "#F6B6B6" },
          { key: "micro_stress_percentage", name: "Micro Stress", color: "#D6E8DB" },
          { key: "ongoing_health_condition_percentage", name: "Ongoing Health Condition", color: "#FFE0C7" },
          { key: "menstration_percentage", name: "Menstration", color: "#F5CEDC" },
          { key: "parental_percentage", name: "Parental", color: "#C3DDC3" },
          { key: "pregnancy_percentage", name: "Pregnancy Related", color: "#E7D1F4" },
          { key: "mental_health_percentage", name: "Mental Health Rest Day", color: "#e8c7fd" },
          { key: "unknown_percentage", name: "Unknown", color: "#c9d6e3" },
        ];

        const rsp = jsonData.ReasonPercentage || {};
        reasonTypes.forEach((reason) => {
          if (rsp[reason.key] !== undefined && rsp[reason.key] > 0) {
            availableReasons.push({ name: reason.name, percentage: rsp[reason.key], color: reason.color });
            if (rsp[reason.key] > mainFactorPercentage) {
              mainFactor = reason.name;
              mainFactorPercentage = rsp[reason.key];
            }
          }
        });
      }

      // Format the dates
      const startDate = new Date(parseInt(jsonData.start_date) * 1000);
      const endDate = new Date(parseInt(jsonData.end_date) * 1000);
      const period = `${startDate.toLocaleDateString()} - ${endDate.toLocaleDateString()}`;

      // Extract the most recent evaluation
      const latestEvaluation = jsonData.Evaluation && jsonData.Evaluation.length > 0 ? jsonData.Evaluation[0] : null;

      // Transform data
      processedData = {
        period,
        organizationName: jsonData.organization_name || "ORGANIZATION",
        introText: "Annual wellbeing report based on collected staff data",
        workplaceHealth: { overall, settled, unsettled },
        highestEmotion: { name: highestEmotionName, percentage: highestEmotionPercentage },
        emotionsList,
        lowMoodFactors: { mainFactor, mainFactorPercentage, availableReasons },
        evaluation: latestEvaluation ? latestEvaluation.evaluation : "",
        action: latestEvaluation ? latestEvaluation.action : "",
      };
    }
  } catch (error) {
    console.error("Error processing data for PDF:", error);
    // Continue with whatever data we have
  }

  // Now use the processed data in the same way the InfoGraphicReportPreview component does
  const organizationName = processedData.organizationName || "ORGANIZATION";
  const period = processedData.period || "";
  const introText = processedData.introText || "Annual wellbeing report based on collected staff data";

  // Workplace health data
  const overall = processedData.workplaceHealth?.overall || 0;
  const settled = processedData.workplaceHealth?.settled || 0;
  const unsettled = processedData.workplaceHealth?.unsettled || 0;

  // Highest emotion data
  const highestEmotionName = processedData.highestEmotion?.name || "None";
  const highestEmotionPercentage = processedData.highestEmotion?.percentage || 0;

  // Emotions data (dynamic list of all feelings)
  const emotionsList = processedData.emotionsList || [];

  // Low mood factors data
  const mainFactor = processedData.lowMoodFactors?.mainFactor || "None";
  const mainFactorPercentage = processedData.lowMoodFactors?.mainFactorPercentage || 0;

  // Prepare segments for pie chart
  const reasons = processedData.lowMoodFactors?.availableReasons || [];
  const pieSegments = reasons.map(reason => ({
    percentage: reason.percentage || 0,
    color: reason.color || "#ccc"
  }));

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <View style={styles.container}>
          {/* Report Header */}
          <View style={styles.header}>
            <View style={styles.badge}>
              <Text style={styles.badgeText}>STATISTICAL INFOGRAPHIC</Text>
            </View>
            <Text style={styles.dateText}>{period}</Text>
          </View>

          {/* Title */}
          <Text style={styles.title}>OVERVIEW FOR {organizationName}</Text>
          <Text style={styles.subtitle}>{introText}</Text>

          {/* Workplace Health */}
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>
              Overall workplace health: {overall}%
            </Text>
            <ProgressBar
              settledPercentage={settled}
              unsettledPercentage={unsettled}
            />
          </View>

          {/* Emotions Card */}
          <View style={styles.card}>
            {/* Split into separate Text elements for proper styling */}
            <View style={{ flexDirection: 'row', marginBottom: 8 }}>
              <Text style={{ fontSize: 16, fontWeight: 'bold', color: '#1F2937' }}>
                Highest ranking emotion:{' '}
              </Text>
              <Text style={{ fontSize: 16, color: '#4B5563' }}>
                {highestEmotionName}
              </Text>
            </View>

            {/* Split the percentage text to allow bold styling */}
            <View style={{ flexDirection: 'row', marginBottom: 24 }}>
              <Text style={{ fontSize: 12, fontWeight: 'bold', color: '#4B5563' }}>
                {highestEmotionPercentage}%
              </Text>
              <Text style={{ fontSize: 12, color: '#4B5563' }}>
                {' of the team reported feeling '}{highestEmotionName.toLowerCase()}{', '}
                {'indicating overall positive workplace culture!'}
              </Text>
            </View>

            <View style={styles.emotionsContainer}>
              {emotionsList.map((emotion, index) => (
                <EmotionCircle
                  key={index}
                  percentage={emotion.percentage}
                  color={emotion.color}
                  label={emotion.name}
                  count={emotion.count}
                />
              ))}
            </View>
          </View>

          <View style={styles.divider} />

          {/* Low Mood Contributors */}
          <View style={styles.card}>
            <Text style={styles.sectionTitle}>
              Biggest contributor to low mood: <Text style={styles.boldText}>{mainFactor}</Text>
            </Text>

            <Text style={styles.descriptionText}>
              <Text style={styles.boldText}>{mainFactorPercentage}%</Text> of reported low mood issues
              were related to {mainFactor.toLowerCase()}.
            </Text>

            <View style={styles.row}>
              <View style={styles.pieContainer}>
                <PieChart segments={pieSegments} />
              </View>
              <View style={styles.legendContainer}>
                {reasons.map((reason, index) => (
                  <View key={index} style={styles.legendItem}>
                    {/* Icon embedded as a data URI (real icon or fileImage placeholder) */}
                    {reason.icon_data ? <Image src={reason.icon_data} style={styles.legendIcon} /> : null}
                    <Text style={styles.legendText}>
                      {reason.name.toUpperCase()} ({reason.percentage}%)
                    </Text>
                  </View>
                ))}
              </View>
            </View>
          </View>
        </View>
      </Page>
    </Document>
  );
};

export default InfoGraphicPdfGenerator;